import { type ConstraintRelation, ConstraintValidity } from "@eu.mdotm.api/sphere";
import { PercentageInput } from "$root/components/PercentageInput";
import { useLocaleFormatters } from "$root/localization/hooks";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { multiRef } from "$root/utils";
import { unreachable } from "@mdotm/utils/runtime";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { type StylableProps, AutoTooltip, Icon, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { NumberInput, type NumberInputProps, Select } from "@mdotm/shadcn/mdotm/components";
import {
	type ClassList,
	type NodeOrFn,
	renderNodeOrFn,
	toClassListRecord,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { hash } from "immutable";
import { type ReactNode, useMemo } from "react";
import { type Control, type ControllerFieldState, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EditPortfolioV4Context } from "../EditPortfolio.shared-types";
import type { EditablePortfolioConstraintHelper, PortfolioConstraintTypePayloadMap } from "../shared";
import { applicationModeOptions, excludeRelationOption, relationOptions, type RelationOption } from "./const";
import equal from "fast-deep-equal";

type AllowedConstraintRelationKeys = Exclude<
	keyof PortfolioConstraintTypePayloadMap,
	"lockInstrumentGroup" | "instrumentMaxWeightConstraint" | "compositionPriority"
>;

type AllowedConstraintRelation = {
	[K in AllowedConstraintRelationKeys]: {
		control: Control<{ list: Array<EditablePortfolioConstraintHelper<K>> }>;
		item: EditablePortfolioConstraintHelper<K>;
		index: number;
		mode: "read" | "edit" | "ai"; // item.readonly || !editable
		/**
		 * @default percentage
		 */
		type?: "number" | "percentage";
		overrideRelationOptions?: RelationOption[];
		originalValues?: {
			relation: ConstraintRelation;
			minWeight: number | null;
			maxWeight: number | null;
		};
		onConstraintChange?(): void; // fix-me: props out of scope but it should fix from start
	};
};

function ConstraintRelationBlock<K extends AllowedConstraintRelationKeys>(
	props: AllowedConstraintRelation[K],
): React.JSX.Element {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const { t } = useTranslation();
	const { resetField } = useFormContext();
	const { item, mode, control, index, overrideRelationOptions, onConstraintChange } = props;

	const investableUniverseCtx = ctx.getStepsData().investableUniverse;

	//trigger sideEffect to recompute range input
	const constraintHash = hash(item);
	return (
		<div className="flex flex-1 space-x-4 mr-4 justify-end">
			{(item.type === "customAssetClassConstraint" ||
				item.type === "customCurrencyConstraint" ||
				// item.type === "customGroupAssetClassConstraints" ||
				item.type === "customTagConstraint" ||
				item.type === "customScoreConstraint") && (
				<div className="w-28 mr-auto">
					{mode === "read" ||
					// workaround(MDOT-22524): hide "Each instrument" option as it's currently not supported
					item.type === "customScoreConstraint" ? (
						<ConstraintLabel label="Apply to" className="w-full">
							{t(`APPLY_TO.${(item as any).applicationMode}` as any)}
						</ConstraintLabel>
					) : (
						<FormController
							control={control}
							name={`list.${index}.applicationMode` as any}
							render={({ field: { ref: fieldRef, ...fieldProps } }) => (
								<ConstraintLabel label="Apply to">
									<Select
										data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/applicationMode`}
										{...fieldProps}
										innerRef={fieldRef}
										options={applicationModeOptions}
										size="x-small"
										onChange={fieldProps.onChange}
										className="w-full"
									/>
								</ConstraintLabel>
							)}
						/>
					)}
				</div>
			)}
			<div className="w-28">
				{mode === "read" ? (
					<ConstraintLabel label="Operator" className="w-full">
						{t(`RELATION.${item.relation}`)}
					</ConstraintLabel>
				) : (
					<FormController
						control={control}
						name={`list.${index}.relation` as any}
						render={({ field: { ref: fieldRef, ...fieldProps } }) => (
							<ConstraintLabel label="Operator">
								<Select
									key={constraintHash}
									data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/relation`}
									{...fieldProps}
									innerRef={fieldRef}
									options={relationOptionsProvider({
										overrideRelationOptions,
										isReferenceAvailable: ctx.stepsData?.mainInfo.investmentReference?.identifier != null,
										isConstraintAppliedOnEachInstrument:
											"applicationMode" in item ? item.applicationMode === "EACH_INSTRUMENT" : undefined,
									})}
									size="x-small"
									onChange={(constraint) => {
										fieldProps.onChange(constraint);
										resetField(`list.${index}.maxWeight`, { defaultValue: null });
										resetField(`list.${index}.minWeight`, { defaultValue: null });
										// <INC-47>: in some cases, resetField puts undefined as target doesn't have an associated default value for the specified index
										resetField(`list.${index}.target`, {
											// also used in src/components/Portfolio/CreatePortfolio/Steps/03-PortfolioConstraints.tsx
											defaultValue: investableUniverseCtx.type === "GROUP_UNIVERSE",
										});
										// if (
										// 	constraint === ConstraintRelation.RelativeToReference &&
										// 	investableUniverseCtx.type === "GROUP_UNIVERSE"
										// ) {
										// 	setValue(`list.${index}.target`, false);
										// } else {
										// 	resetField(`list.${index}.target`);
										// }
										// </INC-47>
										onConstraintChange?.();
									}}
									className="w-full"
								/>
							</ConstraintLabel>
						)}
					/>
				)}
			</div>
			<div
				className={toClassName({
					"min-w-[196px] flex": true,
				})}
			>
				{(() => {
					switch (item.relation) {
						case "BETWEEN":
							return (
								<>
									<WeightController ctx={props} name="minWeight" label="Min" />
									<WeightController ctx={props} name="maxWeight" label="Max" />
								</>
							);
						case "MAX":
							return <WeightController ctx={props} name="maxWeight" label="Max" />;
						case "MIN":
							return <WeightController ctx={props} name="minWeight" label="Min" />;
						case "EQUAL":
							return <WeightController ctx={props} name="minWeight" />;
						case "EXCLUDE":
							return <></>;
						case "RELATIVE_TO_REFERENCE":
							return (
								<>
									<WeightController ctx={props} name="minWeight" label="Δ Min" />
									<WeightController ctx={props} name="maxWeight" label="Δ Max" />
								</>
							);
						default:
							unreachable(item.relation);
					}
				})()}
			</div>
		</div>
	);
}

function WeightController<K extends AllowedConstraintRelationKeys>(props: {
	ctx: AllowedConstraintRelation[K];
	name: `minWeight` | `maxWeight`;
	label?: string;
}) {
	const { ctx, name, label } = props;
	const { mode, type = "percentage", item, control, index } = ctx;
	return (
		<FormController
			control={control}
			name={`list.${index}.${name}` as any}
			render={({ field: { ref, ...fieldProps }, fieldState }) => {
				const error = Boolean(fieldState.error?.message);
				const invalid = isInvalid({ error, validity: item.validity, mode });
				return (
					<ConstraintTooltip ctx={ctx} invalid={invalid} fieldState={fieldState}>
						{({ innerRef }) => (
							<ConstraintInput
								{...fieldProps}
								mode={mode}
								type={type}
								innerRef={multiRef(ref, innerRef)}
								label={label}
								invalid={invalid}
								onChange={fieldProps.onChange}
								onBlur={(e) => {
									fieldProps.onBlur();
									if (!equal(String(e.currentTarget.value), String(fieldProps.value))) {
										ctx.onConstraintChange?.();
									}
								}}
								data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/${name}`}
								{...(item.relation === "RELATIVE_TO_REFERENCE"
									? { min: -100, placeholder: name === "minWeight" ? "-100" : "100" }
									: {})}
							/>
						)}
					</ConstraintTooltip>
				);
			}}
		/>
	);
}

type ConstraintInputProps = {
	mode: "read" | "edit" | "ai"; // item.readonly || !editable
	type: "number" | "percentage";
	label?: string;
} & NumberInputProps;

function ConstraintInput({ label, type, mode, ...inputProps }: ConstraintInputProps) {
	const { formatNumber } = useLocaleFormatters();
	const nullableInputProps = useMemo<NumberInputProps>(
		() => ({
			...inputProps,
			inputAppearance: inputProps.invalid ? undefined : solveWithAiInputStyle(mode === "ai"),
			size: "x-small",
		}),
		[inputProps, mode],
	);

	if (mode === "read") {
		return (
			<ConstraintLabel label={label} align="right" className="w-20 ml-auto text-right">
				{inputProps.value != null ? `${formatNumber(inputProps.value, 2)}${type === "percentage" ? "%" : ""}` : ""}
			</ConstraintLabel>
		);
	}

	return (
		<ConstraintLabel label={label} align="right" className="w-20 ml-auto">
			{type === "percentage" ? <PercentageInput {...nullableInputProps} /> : <NumberInput {...nullableInputProps} />}
		</ConstraintLabel>
	);
}

function ConstraintLabel({
	label,
	children,
	className,
	align = "left",
}: {
	label?: string;
	children: ReactNode;
	className?: ClassList;
	align?: "left" | "right";
}) {
	return (
		<div className={toClassName({ ...toClassListRecord(className) })}>
			{label ? (
				<Text
					type="Body/M/Book"
					as="p"
					className={toClassName({
						"pb-2": true,
						"text-right": align === "right",
						"text-left": align === "left",
					})}
				>
					{label}
				</Text>
			) : (
				<div className="h-[26px] pb-2" />
			)}
			{children}
		</div>
	);
}

type ConstraintTooltipProps<K extends AllowedConstraintRelationKeys> = {
	ctx: AllowedConstraintRelation[K];
	invalid: boolean;
	children: NodeOrFn<{ innerRef: (instance: HTMLElement | null) => void }>;
	fieldState: ControllerFieldState;
};

function ConstraintTooltip<K extends AllowedConstraintRelationKeys>(props: ConstraintTooltipProps<K>) {
	const { invalid, children, fieldState } = props;
	const { mode } = props.ctx;
	return (
		<AutoTooltip
			severity="error"
			position="left"
			mode="hover"
			disabled={mode === "ai" ? undefined : !invalid}
			overrideColor={mode !== "ai" || invalid ? undefined : themeCSSVars.palette_graph_B500}
			trigger={({ innerRef }) => renderNodeOrFn(children, { innerRef })}
		>
			<ConstraintTooltipContent
				item={props.ctx.item}
				mode={props.ctx.mode}
				originalValues={props.ctx.originalValues}
				type={props.ctx.type}
				fieldState={fieldState}
			/>
		</AutoTooltip>
	);
}

type ConstraintTooltipContentProps = {
	fieldState: ControllerFieldState;
	item: {
		suggestedMinWeight?: number | null;
		suggestedMaxWeight?: number | null;
		relation: ConstraintRelation;
	};
	originalValues?: {
		minWeight: number | null;
		maxWeight: number | null;
		relation: ConstraintRelation;
	};
	mode: "read" | "edit" | "ai";
	type?: "number" | "percentage";
};

function ConstraintTooltipContent({
	type = "percentage",
	mode,
	originalValues,
	item,
	fieldState,
}: ConstraintTooltipContentProps) {
	const { formatNumber } = useLocaleFormatters();

	const suggestedMin = formatSolution(formatNumber(item.suggestedMinWeight, 2), type);
	const suggestedMax = formatSolution(formatNumber(item.suggestedMaxWeight, 2), type);
	const suggestionByRelation = {
		MIN: <ClosestFeasibleSolution min={suggestedMin} />,
		MAX: <ClosestFeasibleSolution max={suggestedMax} />,
		BETWEEN: <ClosestFeasibleSolution min={suggestedMin} max={suggestedMax} />,
		EQUAL: (
			<>
				<ClosestFeasibleSolution />,
				{item.suggestedMinWeight != null ? (
					<ClosestFeasibleSolution />
				) : item.suggestedMaxWeight != null ? (
					<ClosestFeasibleSolution />
				) : (
					<></>
				)}
			</>
		),
		EXCLUDE: <></>,
		RELATIVE_TO_REFERENCE: <ClosestFeasibleSolution delta min={suggestedMin} max={suggestedMax} />,
	};

	if (fieldState.error?.message) {
		return <TooltipContent>{fieldState.error.message}</TooltipContent>;
	}

	if (mode === "ai") {
		const min = formatSolution(formatNumber(originalValues?.minWeight, 2), type);
		const max = formatSolution(formatNumber(originalValues?.maxWeight, 2), type);
		const initialValuesByRelation = {
			MIN: <OriginalValues min={min} />,
			MAX: <OriginalValues max={max} />,
			BETWEEN: <OriginalValues min={min} max={max} />,
			EQUAL: (
				<>
					<OriginalValues />,
					{item.suggestedMinWeight != null ? (
						<OriginalValues />
					) : item.suggestedMaxWeight != null ? (
						<OriginalValues />
					) : (
						<></>
					)}
				</>
			),
			EXCLUDE: <></>,
			RELATIVE_TO_REFERENCE: <OriginalValues delta min={min} max={max} />,
		};

		return (
			<TooltipContent>
				<div className="flex gap-2">
					<Icon icon="Ask-ai" color={themeCSSVars.palette_B500} />
					<Text as="span" type="Body/M/Bold">
						Feasibility Adjustments Applied
					</Text>
				</div>
				{originalValues && initialValuesByRelation[item.relation]}
				{suggestionByRelation[item.relation]}
			</TooltipContent>
		);
	}

	return (
		<TooltipContent>
			<p className="font-bold mb-2">Constraint not feasible</p>
			{suggestionByRelation[item.relation]}
		</TooltipContent>
	);
}

type RangeProps = {
	delta?: boolean;
	min?: string;
	max?: string;
};

function RangeBound(props: { label: string; value: string }) {
	return (
		<>
			<i>{props.label}</i>: {props.value}
		</>
	);
}

function Range(props: RangeProps) {
	return (
		<>
			{props.min ? <RangeBound label={`${props.delta ? "Δ" : ""} Min`} value={props.min} /> : <></>}{" "}
			{props.max ? <RangeBound label={`${props.delta ? "Δ" : ""} Max`} value={props.max} /> : <></>}
		</>
	);
}

function ClosestFeasibleSolution(props: RangeProps) {
	return (
		<p>
			Closest feasible solution - <Range {...props} />
		</p>
	);
}

function OriginalValues(props: RangeProps) {
	return (
		<p>
			Original values - <Range {...props} />
		</p>
	);
}

function formatSolution<K extends AllowedConstraintRelationKeys>(
	value: string,
	type: AllowedConstraintRelation[K]["type"],
) {
	const symbol = type === "percentage" ? "%" : "";
	return `${value}${symbol}`;
}

function isInvalid<K extends AllowedConstraintRelationKeys>(params: {
	error: boolean;
	validity: ConstraintValidity;
	mode: AllowedConstraintRelation[K]["mode"];
}): boolean {
	if (params.mode === "ai") {
		return params.error && params.validity === ConstraintValidity.Invalid;
	}

	return params.error || params.validity === ConstraintValidity.Invalid;
}

function solveWithAiInputStyle(ai: boolean): StylableProps | undefined {
	return ai ? { style: { borderColor: themeCSSVars.palette_graph_B500 } } : undefined;
}

function relationOptionsProvider(params: {
	isReferenceAvailable: boolean;
	isConstraintAppliedOnEachInstrument?: boolean;
	overrideRelationOptions?: RelationOption[];
}) {
	if (params.isConstraintAppliedOnEachInstrument) {
		return excludeRelationOption(
			["RELATIVE_TO_REFERENCE"],
			params.overrideRelationOptions ? params.overrideRelationOptions : relationOptions,
		);
	}

	if (params.overrideRelationOptions) {
		return params.overrideRelationOptions;
	}

	if (params.isReferenceAvailable) {
		return relationOptions;
	}

	return excludeRelationOption(["RELATIVE_TO_REFERENCE"]);
}

export { ConstraintRelationBlock, isInvalid, solveWithAiInputStyle, ConstraintTooltipContent };
